import React, { useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import TestRide from './pages/TestRide';
import BookNow from './pages/BookNow';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import { AuthProvider, useAuth } from './context/AuthContext';

// Admin Components
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProductsManagement from './pages/admin/ProductsManagement';
import BookingsManagement from './pages/admin/BookingsManagement';
import TestRidesManagement from './pages/admin/TestRidesManagement';
import UsersManagement from './pages/admin/UsersManagement';

// Additional Pages
import Dealership from './pages/Dealership';
import Support from './pages/Support';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Contact from './pages/Contact';
import Warranty from './pages/Warranty';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// Protected Route Component (for regular users)
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Protected Admin Route Component
const ProtectedAdminRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAuth = () => {
      const adminToken = localStorage.getItem('admin_token');
      const adminUser = localStorage.getItem('admin_user');
      
      if (adminToken && adminUser) {
        try {
          const user = JSON.parse(adminUser);
          if (user.role === 'admin') {
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Error parsing admin user:', error);
        }
      }
      setLoading(false);
    };
    
    checkAdminAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes with Navbar and Footer */}
          <Route path="/" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Home />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/products" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Products />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/product/:id" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <ProductDetail />
              </main>
              <Footer />
            </div>
          } />
          
          {/* Test Ride Routes - IMPORTANT: Both routes must be defined */}
          <Route path="/test-ride" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <TestRide />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/test-ride/:id" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <TestRide />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/book/:id" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <BookNow />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/login" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Login />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/register" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Register />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow">
                  <Dashboard />
                </main>
                <Footer />
              </div>
            </ProtectedRoute>
          } />
          
          {/* Additional Pages */}
          <Route path="/dealership" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Dealership />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/support" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Support />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/faq" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <FAQ />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/about" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <About />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/contact" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Contact />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/warranty" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Warranty />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/privacy" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Privacy />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/terms" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Terms />
              </main>
              <Footer />
            </div>
          } />
          
          {/* Admin Routes - No Navbar/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          
          <Route path="/admin" element={
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<ProductsManagement />} />
            <Route path="bookings" element={<BookingsManagement />} />
            <Route path="test-rides" element={<TestRidesManagement />} />
            <Route path="users" element={<UsersManagement />} />
          </Route>
          
          {/* 404 Not Found Route - Must be last */}
          <Route path="*" element={
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow flex items-center justify-center">
                <div className="text-center py-16">
                  <div className="text-6xl mb-6">🔍</div>
                  <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                  <h2 className="text-2xl font-semibold mb-4">Page Not Found</h2>
                  <p className="text-gray-600 mb-8">The page you are looking for doesn't exist or has been moved.</p>
                  <Link to="/" className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition">
                    Go Back Home
                  </Link>
                </div>
              </main>
              <Footer />
            </div>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;